import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Modal, ProgressBar, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import * as FileSaver from "file-saver";
import EmpleadosService from "../services/empleados.service";
import CajasService from "../services/cajas.service";
import PagosCuentasCorrientesService from "../services/pagos_cuentas_corrientes.service";
import "./dashboardCajas.css";
import MUIDataTable from "mui-datatables";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Visibility from "@mui/icons-material/Visibility";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
const dayjs = require("dayjs");

const ExcelJS = require("exceljs");

const nf = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DashboardCajas = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cajasAbiertas, setCajasAbiertas] = useState([]);
  const containerRef = useRef(null);
  const [showModalEmpleado, setShowModalEmpleado] = useState(false);
  const [showPregunta, setShowPregunta] = useState(false);
  const [showInformeCaja, setShowInformeCaja] = useState(false);
  const [cajaActual, setCajaActual] = useState({});
  const [historialDeCajas, setHistorialDeCajas] = useState([]);
  const [dataElegido, setDataElegido] = useState([]);
  const [historialPagosCaja, setHistorialPagosCaja] = useState([]);
  const [informeCaja, setInformeCaja] = useState([]);
  const [dataCajaCerrada, setDataCajaCerrada] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [showComprobantePagoCC, setShowComprobantePagoCC] = useState(false);
  const [dataGetPago, setDataGetPago] = useState(false);
  const [activeTab, setActiveTab] = useState("actual");

  const fetchCajas = async () => {
    setLoading(true);
    setError("");

    try {
      const responseEmpleados = await EmpleadosService.getAllEmpleados();
      const empleados = responseEmpleados.data || [];

      const requests = empleados.map((empleado) =>
        CajasService.searchCaja(empleado.CodigoVendedor)
      );

      const results = await Promise.allSettled(requests);

      const abiertas = results
        .map((result, index) => {
          if (result.status !== "fulfilled") {
            return null;
          }

          const caja = result.value?.data;
          if (!caja || !caja.montos) {
            return null;
          }

          const empleado = empleados[index];

          return {
            idCaja: caja.idCaja,
            codigoVendedor: empleado?.CodigoVendedor,
            nombreEmpleado: caja.user?.empleado?.Nombre || empleado?.Nombre || "-",
            apellidoEmpleado: caja.user?.empleado?.Apellido || empleado?.Apellido || "",
            montos: {
              efectivo: safeNumber(caja.montos.montoEfectivo),
              transferencia: safeNumber(caja.montos.montoTransferencia),
              cheque: safeNumber(caja.montos.montoCheque),
              tarjeta: safeNumber(caja.montos.montoTarjeta),
              total: safeNumber(caja.montos.montoTotal),
            },
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.montos.total - a.montos.total);

      setCajasAbiertas(abiertas);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "No pudimos cargar el dashboard de cajas."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      fetchCajas();
    }

    return () => {
      mounted = false;
    };
  }, []);

  const resumen = useMemo(() => {
    return cajasAbiertas.reduce(
      (acc, caja) => {
        acc.efectivo += caja.montos.efectivo;
        acc.transferencia += caja.montos.transferencia;
        acc.cheque += caja.montos.cheque;
        acc.tarjeta += caja.montos.tarjeta;
        acc.total += caja.montos.total;
        return acc;
      },
      {
        efectivo: 0,
        transferencia: 0,
        cheque: 0,
        tarjeta: 0,
        total: 0,
      }
    );
  }, [cajasAbiertas]);

  const tipos = [
    {
      key: "efectivo",
      label: "Efectivo",
      icon: "EF",
      color: "#1d7a4f",
      bg: "linear-gradient(135deg, #f3fbf6 0%, #e8f6ee 100%)",
      value: resumen.efectivo,
    },
    {
      key: "transferencia",
      label: "Transferencia",
      icon: "TR",
      color: "#1f4d8f",
      bg: "linear-gradient(135deg, #f1f6fd 0%, #e4eefb 100%)",
      value: resumen.transferencia,
    },
    {
      key: "cheque",
      label: "Cheque",
      icon: "CH",
      color: "#945d1f",
      bg: "linear-gradient(135deg, #fdf8f1 0%, #faefde 100%)",
      value: resumen.cheque,
    },
    {
      key: "tarjeta",
      label: "Tarjeta",
      icon: "TJ",
      color: "#6c3c8a",
      bg: "linear-gradient(135deg, #fbf6ff 0%, #f3e8ff 100%)",
      value: resumen.tarjeta,
    },
  ];

  const exportarExcel = async () => {
    const header = [
      "Vendedor",
      "Empleado",
      "Efectivo",
      "Transferencia",
      "Cheque",
      "Tarjeta",
      "Total",
    ];

    const detalleRows = cajasAbiertas.map((caja) => [
      caja.codigoVendedor ?? "",
      `${caja.nombreEmpleado} ${caja.apellidoEmpleado}`.trim(),
      caja.montos.efectivo,
      caja.montos.transferencia,
      caja.montos.cheque,
      caja.montos.tarjeta,
      caja.montos.total,
    ]);

    const resumenRows = [
      ["", "Saldo general", resumen.total],
      ["", "Efectivo", resumen.efectivo],
      ["", "Transferencia", resumen.transferencia],
      ["", "Cheque", resumen.cheque],
      ["", "Tarjeta", resumen.tarjeta],
    ];

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Dashboard");

    const fechaActual = new Date().toLocaleDateString("es-AR");
    ws.getCell("A1").value = `Cajas Abiertas Día ${fechaActual}`;

    ws.addRow(header);

    detalleRows.forEach((row) => {
      ws.addRow(row);
    });

    ws.addRow([]);
    const resumenStartRow = ws.lastRow.number + 1;
    resumenRows.forEach((row) => {
      ws.addRow(row);
    });

    ws.eachRow((row) => {
      row.eachCell((cell) => {
        cell.font = {
          ...cell.font,
          name: "Arial",
          size: 16,
          bold: false,
        };
      });
    });

    const tituloCell = ws.getCell("A1");
    tituloCell.font = { ...tituloCell.font, bold: true, size: 18, name: "Arial" };

    const headerRow = ws.getRow(2);
    headerRow.eachCell((cell) => {
      cell.font = { ...cell.font, bold: true, size: 16, name: "Arial" };
    });

    for (let rowIndex = 3; rowIndex <= 2 + detalleRows.length; rowIndex += 1) {
      [3, 4, 5, 6, 7].forEach((colIndex) => {
        ws.getCell(rowIndex, colIndex).numFmt = "#,##0.00";
      });
    }

    for (
      let rowIndex = resumenStartRow;
      rowIndex < resumenStartRow + resumenRows.length;
      rowIndex += 1
    ) {
      const labelCell = ws.getCell(rowIndex, 2);
      const valueCell = ws.getCell(rowIndex, 3);

      labelCell.font = { ...labelCell.font, bold: true, size: 16, name: "Arial" };
      valueCell.font = { ...valueCell.font, bold: true, size: 16, name: "Arial" };
      valueCell.numFmt = "#,##0.00";
    }

    for (let colIndex = 1; colIndex <= ws.columnCount; colIndex += 1) {
      const column = ws.getColumn(colIndex);
      let maxLength = 0;

      column.eachCell({ includeEmpty: true }, (cell) => {
        let text = "";

        if (cell.value !== null && cell.value !== undefined) {
          if (typeof cell.value === "object") {
            if (cell.value.richText) {
              text = cell.value.richText.map((item) => item.text).join("");
            } else if (cell.value.text) {
              text = cell.value.text;
            } else if (cell.value.result !== undefined) {
              text = String(cell.value.result);
            } else {
              text = String(cell.value);
            }
          } else {
            text = String(cell.value);
          }
        }

        if (typeof cell.value === "number") {
          text = text + "  ";
        }

        maxLength = Math.max(maxLength, text.length);
      });

      const extra = colIndex === 2 ? 18 : 2;
      const maxWidth = colIndex === 2 ? 520 : 240;
      column.width = Math.min(maxWidth, Math.max(24, maxLength + extra));
    }

    const buffer = await wb.xlsx.writeBuffer();
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const data = new Blob([buffer], { type: fileType });
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

    FileSaver.saveAs(data, `Dashboard-Cajas-${stamp}.xlsx`);
  };

  const nfDecimal = new Intl.NumberFormat("es-AR", {
    style: "decimal",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const cargarModalEmpleado = async (codigoVendedor, nombreCompleto) => {
    setLoadingModal(true);
    try {
      const responseCaja = await CajasService.searchCaja(codigoVendedor);
      let auxCajaActual = responseCaja.data;
      auxCajaActual.montos.montoCheque = nfDecimal.format(
        auxCajaActual.montos.montoCheque
      );
      auxCajaActual.montos.montoEfectivo = nfDecimal.format(
        auxCajaActual.montos.montoEfectivo
      );
      auxCajaActual.montos.montoTarjeta = nfDecimal.format(
        auxCajaActual.montos.montoTarjeta
      );
      auxCajaActual.montos.montoTransferencia = nfDecimal.format(
        auxCajaActual.montos.montoTransferencia
      );
      auxCajaActual.montos.montoTotal = nfDecimal.format(
        auxCajaActual.montos.montoTotal
      );
      setCajaActual(auxCajaActual);
      const responseHistorial = await CajasService.getHistorialCajas(
        codigoVendedor
      );
      setHistorialDeCajas(responseHistorial.data);
      setDataElegido([null, nombreCompleto, codigoVendedor]);
      setActiveTab("actual");
      setShowModalEmpleado(true);
    } catch (err) {
      toast.error(
        <div>
          {(err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Error al cargar los datos de la caja"}
        </div>,
        { position: "top-center", autoClose: 2000 }
      );
      if (err.response && err.response.status === 401) {
        EventBus.dispatch("logout");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const cerrarModal = () => {
    setShowModalEmpleado(false);
    setActiveTab("actual");
  };

  const cargarModalPago = (rowData) => {
    setLoadingModal(true);
    PagosCuentasCorrientesService.getPago(rowData[0]).then(
      (response) => {
        let auxDataGetPago = response.data;
        const nfLocal = new Intl.NumberFormat("es-AR", {
          style: "decimal",
          currency: "ARS",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        auxDataGetPago.ChequeMonto = nfLocal.format(auxDataGetPago.ChequeMonto);
        auxDataGetPago.EfectivoMonto = nfLocal.format(auxDataGetPago.EfectivoMonto);
        auxDataGetPago.TarjetaMonto = nfLocal.format(auxDataGetPago.TarjetaMonto);
        auxDataGetPago.TransferenciaMonto = nfLocal.format(auxDataGetPago.TransferenciaMonto);
        auxDataGetPago.Total = nfLocal.format(auxDataGetPago.Total);
        setDataGetPago(auxDataGetPago);
        setLoadingModal(false);
        setShowComprobantePagoCC(true);
      },
      (error) => {
        setLoadingModal(false);
        toast.error(
          <div>
            {(error.response && error.response.data && error.response.data.message) ||
              error.message ||
              "Error al cargar el pago"}
          </div>,
          { position: "top-center", autoClose: 2000 }
        );
        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
        }
      }
    );
  };

  const eliminarPago = () => {
    setLoadingModal(true);
    PagosCuentasCorrientesService.eliminarPago(dataGetPago.idPago).then(response => {
      if (response.status === 200) {
        toast.success(<div>Pago eliminado correctamente</div>, {
          position: "top-center",
          autoClose: 2000,
        });
        setShowComprobantePagoCC(false);
        setLoadingModal(false);
        cargarModalEmpleado(dataElegido[2], dataElegido[1]);
      } else {
        setLoadingModal(false);
        toast.error(<div>Error al eliminar el pago</div>, {
          position: "top-center",
          autoClose: 2000,
        });
      }
    }).catch(error => {
      setLoadingModal(false);
      toast.error(
        <div>Error al eliminar el pago: {error.response?.data?.message || error.message}</div>,
        { position: "top-center", autoClose: 2000 }
      );
    });
  };

  const cerrarModalCierraCaja = async () => {
    if (!cajaActual.pagos || cajaActual.pagos.length === 0) {
      toast.error(<div>No se puede cerrar una caja sin movimientos</div>, {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        whiteSpace: "pre-line",
      });
      setShowPregunta(false);
      return;
    }
    let observacion = document.getElementById("observacion-dashboard").value;
    setLoadingModal(true);
    try {
      await CajasService.cerrarCaja(dataElegido[2], observacion);
      toast.success(<div>Caja cerrada correctamente</div>, {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        whiteSpace: "pre-line",
      });
      const responseCaja = await CajasService.searchCaja(dataElegido[2]);
      let auxCajaActual = responseCaja.data;
      auxCajaActual.montos.montoCheque = nfDecimal.format(
        auxCajaActual.montos.montoCheque
      );
      auxCajaActual.montos.montoEfectivo = nfDecimal.format(
        auxCajaActual.montos.montoEfectivo
      );
      auxCajaActual.montos.montoTarjeta = nfDecimal.format(
        auxCajaActual.montos.montoTarjeta
      );
      auxCajaActual.montos.montoTransferencia = nfDecimal.format(
        auxCajaActual.montos.montoTransferencia
      );
      auxCajaActual.montos.montoTotal = nfDecimal.format(
        auxCajaActual.montos.montoTotal
      );
      setCajaActual(auxCajaActual);
      const responseHistorial = await CajasService.getHistorialCajas(
        dataElegido[2]
      );
      setHistorialDeCajas(responseHistorial.data);
      setShowPregunta(false);
      setActiveTab("historicoCajas");
      fetchCajas();
      const obsTextarea = document.getElementById("observacion-dashboard");
      if (obsTextarea) {
        obsTextarea.value = "";
      }
    } catch (err) {
      toast.error(
        <div>
          {(err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Error al cerrar caja"}
        </div>,
        { position: "top-center", autoClose: 2000 }
      );
      if (err.response && err.response.status === 401) {
        EventBus.dispatch("logout");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const cargarInformeCaja = async (rowData) => {
    setInformeCaja(rowData);
    setLoadingModal(true);
    try {
      const response = await CajasService.getHistorialPagosCaja(rowData[0]);
      setDataCajaCerrada(response.data);
      let aux = [];
      response.data.pagos.forEach((pago) => {
        let auxPago = {
          cliente: pago.cuentas_corriente.cliente.RazonSocial,
          tipo:
            pago.Tipo === "Transferencia"
              ? "Pago - " + pago.Tipo + " - " + pago.TransferenciaCuentaDestino
              : "Pago - " + pago.Tipo,
          total: parseFloat(pago.Total),
          observaciones:
            pago.ObservacionesDescuento != null
              ? pago.ObservacionesDescuento
              : pago.ObservacionesNDD != null
              ? pago.ObservacionesNDD
              : pago.Observaciones,
        };
        aux.push(auxPago);
      });
      response.data.pps.forEach((pp) => {
        let auxPP = {
          cliente: pp.cliente.RazonSocial,
          tipo: "Pp",
          total: parseFloat(pp.importe),
          observaciones: "N° de Pp: " + pp.idPp,
        };
        aux.push(auxPP);
      });
      response.data.facturas.forEach((factura) => {
        let auxFactura = {
          cliente: factura.cliente.RazonSocial,
          tipo: "Factura",
          total: parseFloat(factura.Importe),
          observaciones: "N° de Factura: " + factura.idFactura,
        };
        aux.push(auxFactura);
      });
      setHistorialPagosCaja(aux);
      setShowInformeCaja(true);
    } catch (err) {
      toast.error(
        <div>
          {(err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Error al cargar el informe de caja"}
        </div>,
        { position: "top-center", autoClose: 2000 }
      );
      if (err.response && err.response.status === 401) {
        EventBus.dispatch("logout");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const cerrarModalInformeCaja = () => {
    setShowInformeCaja(false);
  };

  const exportarExcelCajaAbierta = async () => {
    setLoadingExcel(true);
    try {
      const responseInformeCajaAbierta = await CajasService.getInformeCajaAbierta(dataElegido[2]);
      let user = JSON.parse(localStorage.getItem("user"));
      let empleado_porcentaje_comision = user.empleado.porcentaje_comision;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Datos', {
        pageSetup: {
          orientation: 'landscape',
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          paperSize: 9,
        },
      });

      const textoEmpleado = "Empleado: " + dataElegido[1];
      const anchoColumnaCliente = Math.max(60, textoEmpleado.length + 2);

      const infoRow1 = worksheet.addRow(["", "", textoEmpleado, "Fecha: Caja Abierta"]);
      infoRow1.font = { size: 15, bold: true };
      infoRow1.height = 27;
      infoRow1.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.addRow({});

      worksheet.columns = [
        { key: 'fecha', width: 12 },
        { key: 'numCliente', width: 20 },
        { key: 'cliente', width: anchoColumnaCliente },
        { key: 'tipo', width: 35 },
        { key: 'total', width: 15 },
        { key: 'observaciones', width: 40 },
        { key: 'usuario', width: 20 },
        { key: 'eliminado', width: 20 }
      ];

      const headerRow = worksheet.addRow(['Fecha', 'N° Cliente', 'Cliente', 'Tipo', 'Total', 'Observaciones', 'Creado (Cod. Vend)', 'Eliminado']);
      headerRow.font = { size: 16, bold: true };
      headerRow.height = 28;

      let todosLosElementos = [];

      responseInformeCajaAbierta.data.clientes.forEach(cliente => {
        cliente.facturas.forEach(factura => {
          todosLosElementos.push({
            fecha: dayjs(factura.FechaCarga).toDate(),
            numCliente: factura.cliente.idCliente,
            cliente: factura.cliente.RazonSocial,
            tipo: "Factura",
            total: parseFloat(factura.Importe),
            observaciones: "N° de Factura: " + factura.idFactura,
            usuario: factura.Empleado || '',
            eliminado: factura.deletedAt != null ? "Si" : "No"
          });
        });
      });

      responseInformeCajaAbierta.data.clientes.forEach(cliente => {
        cliente.pps.forEach(pp => {
          todosLosElementos.push({
            fecha: dayjs(pp.fecha).toDate(),
            numCliente: pp.cliente.idCliente,
            cliente: pp.cliente.RazonSocial,
            tipo: "Pp",
            total: parseFloat(pp.importe),
            observaciones: "N° de Pp: " + pp.idPp,
            usuario: pp.user?.empleado?.CodigoVendedor || '',
            eliminado: pp.deletedAt != null ? "Si" : "No"
          });
        });
      });

      responseInformeCajaAbierta.data.pagos.forEach(pago => {
        todosLosElementos.push({
          fecha: dayjs(pago.Fecha).toDate(),
          numCliente: pago.cuentas_corriente.cliente.idCliente,
          cliente: pago.cuentas_corriente.cliente.RazonSocial,
          tipo: pago.Tipo == "Transferencia" ? "Pago - " + pago.Tipo + " - " + pago.TransferenciaCuentaDestino : "Pago - " + pago.Tipo,
          total: parseFloat(pago.Total),
          observaciones: pago.ObservacionesDescuento != null ? pago.ObservacionesDescuento :
                        pago.ObservacionesNDD != null ? pago.ObservacionesNDD :
                        pago.Observaciones,
          usuario: pago.user?.empleado?.CodigoVendedor || '',
          eliminado: pago.deletedAt != null ? "Si" : "No"
        });
      });

      todosLosElementos.sort((a, b) => a.fecha - b.fecha);

      todosLosElementos.forEach(elemento => {
        worksheet.addRow({
          fecha: dayjs(elemento.fecha).format("DD/MM/YYYY"),
          numCliente: elemento.numCliente,
          cliente: elemento.cliente,
          tipo: elemento.tipo,
          total: elemento.total,
          observaciones: elemento.observaciones,
          usuario: elemento.usuario,
          eliminado: elemento.eliminado
        });
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.font = { size: 14 };
          row.height = 25;
        }
      });

      worksheet.eachRow((row) => {
        if (row.getCell('eliminado').value === "Si") {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFF0000' }
            };
            cell.font = { size: 14, color: { argb: 'FFFFFFFF' } };
          });
        }
      });

      worksheet.addRow({});

      const montosHeaderRow = worksheet.addRow({fecha: "Monto Pagos", tipo: "Billetes Efectivo (Cantidades)"});
      montosHeaderRow.font = { size: 15, bold: true };
      montosHeaderRow.height = 27;
      worksheet.mergeCells(`A${montosHeaderRow.number}:B${montosHeaderRow.number}`);

      const montoTotalSinDescuentos = parseFloat(responseInformeCajaAbierta.data.montos.montoCheque) + parseFloat(responseInformeCajaAbierta.data.montos.montoEfectivo) + parseFloat(responseInformeCajaAbierta.data.montos.montoTarjeta) + parseFloat(responseInformeCajaAbierta.data.montos.montoTransferencia) + parseFloat(responseInformeCajaAbierta.data.montos.montoDebito);
      const montoComision = montoTotalSinDescuentos * empleado_porcentaje_comision / 100;

      const montoRows = [
        { etiqueta: "Efectivo", valor: responseInformeCajaAbierta.data.montos.montoEfectivo },
        { etiqueta: "Transferencias", valor: responseInformeCajaAbierta.data.montos.montoTransferencia },
        { etiqueta: "Cheques", valor: responseInformeCajaAbierta.data.montos.montoCheque },
        { etiqueta: "Tarjetas", valor: responseInformeCajaAbierta.data.montos.montoTarjeta },
        { etiqueta: "Notas de Débito", valor: responseInformeCajaAbierta.data.montos.montoDebito },
        { etiqueta: "Descuento", valor: responseInformeCajaAbierta.data.montos.montoDescuento },
        { etiqueta: "Total Sin Descuentos", valor: montoTotalSinDescuentos },
        { etiqueta: "Porcentaje Comisión %", valor: empleado_porcentaje_comision },
        { etiqueta: "Vuelto", valor: responseInformeCajaAbierta.data.montos.montoVuelto },
        { etiqueta: "Vuelto Empleado", valor: responseInformeCajaAbierta.data.montos.montoVueltoEmpleado },
        { etiqueta: "Total Vueltos", valor: parseFloat(responseInformeCajaAbierta.data.montos.montoVueltoEmpleado) + parseFloat(responseInformeCajaAbierta.data.montos.montoVuelto) },
        
      ];

      const denominacionesBilletes = [20, 50, 100, 200, 500, 1000, 2000, 10000, 20000];
      const cantidadesBilletes = denominacionesBilletes.reduce((acumulador, denominacion) => {
        acumulador[denominacion] = 0;
        return acumulador;
      }, {});

      responseInformeCajaAbierta.data.pagos
        .filter((pago) => pago.Tipo === "Efectivo" && pago.deletedAt == null)
        .forEach((pago) => {
          denominacionesBilletes.forEach((denominacion) => {
            const keyBillete = `b${denominacion}`;
            const cantidad = parseInt(pago[keyBillete] || 0, 10);
            cantidadesBilletes[denominacion] += Number.isNaN(cantidad) ? 0 : cantidad;
          });
        });

      const filasBilletes = [];
      denominacionesBilletes.forEach((denominacion) => {
        const etiquetaBillete = new Intl.NumberFormat("es-AR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(denominacion);
        const subtotalBillete = cantidadesBilletes[denominacion] * denominacion;
        const subtotalBilleteFormateado = new Intl.NumberFormat("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(subtotalBillete);

        filasBilletes.push({
          billete: `$${etiquetaBillete}`,
          cantidad: `${cantidadesBilletes[denominacion]}`,
          subtotal: subtotalBilleteFormateado,
        });
      });

      const cantidadFilasResumen = Math.max(montoRows.length, filasBilletes.length);
      for (let i = 0; i < cantidadFilasResumen; i += 1) {
        const montoData = montoRows[i];
        const billeteData = filasBilletes[i];
        const row = worksheet.addRow([
          montoData?.etiqueta || "",
          "",
          montoData?.valor ?? "",
          billeteData?.billete || "",
          billeteData?.cantidad || "",
          billeteData?.subtotal || "",
        ]);
        if (montoData) {
          worksheet.mergeCells(`A${row.number}:B${row.number}`);
        }
        row.font = { size: 14 };
        row.height = 25;
      }

      const totalRow = worksheet.addRow({
        fecha: "Monto Total Sin Descuentos",
        cliente: parseFloat(responseInformeCajaAbierta.data.montos.montoTotal) - parseFloat(responseInformeCajaAbierta.data.montos.montoDescuento) 
      });
      totalRow.font = { size: 15, bold: true };
      totalRow.height = 27;

      const montoComisionRow = worksheet.addRow({
        fecha: "Monto Comisión",
        cliente: montoComision
      });
      montoComisionRow.font = { size: 15, bold: true };
      montoComisionRow.height = 27;

      worksheet.getColumn('total').numFmt = '#,##0.00';

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(blob, "Caja Abierta " + dayjs().format("DD/MM/YYYY H:m:ss") + ".xlsx");
      setLoadingExcel(false);
    } catch (error) {
      setLoadingExcel(false);
      toast.error(
        <div>Error al exportar a Excel. Por favor, inténtelo de nuevo.</div>,
        { position: "top-center", autoClose: 2000 }
      );
    }
  };

  const exportarExcelCajaCerrada = async () => {
    console.log("Exportar a Excel");
    let user = JSON.parse(localStorage.getItem("user"));
    setLoadingExcel(true);
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Datos', {
        pageSetup: {
          orientation: 'landscape',
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          paperSize: 9
        }
      });

      const empleadoNombre = dataCajaCerrada?.cajaBuscada?.empleado?.Nombre || dataElegido?.[1] || "";
      const empleadoApellido = dataCajaCerrada?.cajaBuscada?.empleado?.Apellido || "";
      const textoEmpleado = "Empleado: " + empleadoNombre + " " + empleadoApellido;
      const anchoColumnaCliente = Math.max(60, textoEmpleado.length + 2);

      const fechaCierre = dataCajaCerrada?.cajaBuscada?.FechaCierre || informeCaja?.[1];
      const infoRow1 = worksheet.addRow([
        "",
        "",
        textoEmpleado,
        "Fecha: " + dayjs(fechaCierre).format("DD/MM/YYYY HH:mm:ss")
      ]);
      infoRow1.font = { size: 15, bold: true };
      infoRow1.height = 27;
      infoRow1.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.addRow({});

      worksheet.columns = [
        { key: 'fecha', width: 15 },
        { key: 'numCliente', width: 15 },
        { key: 'cliente', width: anchoColumnaCliente },
        { key: 'tipo', width: 35 },
        { key: 'total', width: 15 },
        { key: 'observaciones', width: 40 },
        { key: 'usuario', width: 20 },
        { key: 'eliminado', width: 20 }
      ];

      const headerRow = worksheet.addRow(['Fecha', 'N° Cliente', 'Cliente', 'Tipo', 'Total', 'Observaciones', 'Creado Por', 'Eliminado']);
      headerRow.font = { size: 16, bold: true };
      headerRow.height = 28;

      let todosLosElementos = [];

      dataCajaCerrada.facturas.forEach(factura => {
          todosLosElementos.push({
            fecha: dayjs(factura.FechaCarga).toDate(),
            numCliente: factura.cliente.idCliente,
            cliente: factura.cliente.RazonSocial,
            tipo: "Factura",
            total: parseFloat(factura.Importe),
            observaciones: "N° de Factura: " + factura.idFactura,
            usuario: factura.Empleado || '',
            eliminado: factura.deletedAt != null ? "Si" : "No"
          });
      });

      dataCajaCerrada.pps.forEach(pp => {
          todosLosElementos.push({
            fecha: dayjs(pp.fecha).toDate(),
            numCliente: pp.cliente.idCliente,
            cliente: pp.cliente.RazonSocial,
            tipo: "Pp",
            total: parseFloat(pp.importe),
            observaciones: "N° de Pp: " + pp.idPp,
            usuario: pp.user?.empleado?.CodigoVendedor || '',
            eliminado: pp.deletedAt != null ? "Si" : "No"
          });
      });

      dataCajaCerrada.pagos.forEach(pago => {
        todosLosElementos.push({
          fecha: dayjs(pago.Fecha).toDate(),
          numCliente: pago.cuentas_corriente.cliente.idCliente,
          cliente: pago.cuentas_corriente.cliente.RazonSocial,
          tipo: pago.Tipo == "Transferencia" ? "Pago - " + pago.Tipo + " - " + pago.TransferenciaCuentaDestino : "Pago - " + pago.Tipo,
          total: parseFloat(pago.Total),
          observaciones: pago.ObservacionesDescuento != null ? pago.ObservacionesDescuento :
                        pago.ObservacionesNDD != null ? pago.ObservacionesNDD :
                        pago.Observaciones,
          usuario: pago.user?.empleado?.CodigoVendedor || '',
          eliminado: pago.deletedAt != null ? "Si" : "No"
        });
      });

      todosLosElementos.sort((a, b) => a.fecha - b.fecha);

      todosLosElementos.forEach(elemento => {
        worksheet.addRow({
          fecha: dayjs(elemento.fecha).format("DD/MM/YYYY"),
          numCliente: elemento.numCliente,
          cliente: elemento.cliente,
          tipo: elemento.tipo,
          total: elemento.total,
          observaciones: elemento.observaciones,
          usuario: elemento.usuario,
          eliminado: elemento.eliminado
        });
      });

      worksheet.eachRow((row) => {
        if(row.getCell('eliminado').value === "Si") {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFF0000' }
            };
          });
        }
      });

      worksheet.addRow({});
      const montosHeaderRow = worksheet.addRow({fecha: "Monto Pagos", tipo: "Billetes Efectivo (Cantidades)"});
      montosHeaderRow.font = { size: 15, bold: true };
      montosHeaderRow.height = 27;
      worksheet.mergeCells(`A${montosHeaderRow.number}:B${montosHeaderRow.number}`);

      const montoSinDescuentos =
        parseFloat(dataCajaCerrada.montos.montoEfectivo) +
        parseFloat(dataCajaCerrada.montos.montoTransferencia) +
        parseFloat(dataCajaCerrada.montos.montoCheque) +
        parseFloat(dataCajaCerrada.montos.montoTarjeta) +
        parseFloat(dataCajaCerrada.montos.montoDebito);

      const montoRows = [
        {
          etiqueta: "Efectivo",
          valor: dataCajaCerrada.montos.montoEfectivo
        },
        {
          etiqueta: "Transferencias",
          valor: dataCajaCerrada.montos.montoTransferencia
        },
        {
          etiqueta: "Cheques",
          valor: dataCajaCerrada.montos.montoCheque
        },
        {
          etiqueta: "Tarjetas",
          valor: dataCajaCerrada.montos.montoTarjeta
        },
        {
          etiqueta: "Notas de Débito",
          valor: dataCajaCerrada.montos.montoDebito
        },
        {
          etiqueta: "Descuento",
          valor: dataCajaCerrada.montos.montoDescuento
        },
        {
          etiqueta: "Total Sin Descuentos",
          valor: montoSinDescuentos
        },
        {
          etiqueta: "Porcentaje Comisión %",
          valor: user.empleado.porcentaje_comision
        }
      ];

      const denominacionesBilletes = [20, 50, 100, 200, 500, 1000, 2000, 10000, 20000];
      const cantidadesBilletes = denominacionesBilletes.reduce((acumulador, denominacion) => {
        acumulador[denominacion] = 0;
        return acumulador;
      }, {});

      dataCajaCerrada.pagos
        .filter((pago) => pago.Tipo === "Efectivo" && pago.deletedAt == null)
        .forEach((pago) => {
          denominacionesBilletes.forEach((denominacion) => {
            const keyBillete = `b${denominacion}`;
            const cantidad = parseInt(pago[keyBillete] || 0, 10);
            cantidadesBilletes[denominacion] += Number.isNaN(cantidad) ? 0 : cantidad;
          });
        });

      const filasBilletes = [];

      denominacionesBilletes.forEach((denominacion) => {
        const etiquetaBillete = new Intl.NumberFormat("es-AR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(denominacion);
        const subtotalBillete = cantidadesBilletes[denominacion] * denominacion;
        const subtotalBilleteFormateado = new Intl.NumberFormat("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(subtotalBillete);

        filasBilletes.push({
          billete: `$${etiquetaBillete}`,
          cantidad: `${cantidadesBilletes[denominacion]}`,
          subtotal: subtotalBilleteFormateado,
        });
      });

      const cantidadFilasResumen = Math.max(montoRows.length, filasBilletes.length);

      for (let i = 0; i < cantidadFilasResumen; i += 1) {
        const montoData = montoRows[i];
        const billeteData = filasBilletes[i];

        const row = worksheet.addRow([
          montoData?.etiqueta || "",
          "",
          montoData?.valor ?? "",
          billeteData?.billete || "",
          billeteData?.cantidad || "",
          billeteData?.subtotal || "",
        ]);

        if (montoData) {
          worksheet.mergeCells(`A${row.number}:B${row.number}`);
        }

        row.font = { size: 14 };
        row.height = 25;
      }

      worksheet.addRow({
        tipo: "Monto Total Con Descuentos",
        total: dataCajaCerrada.montos.montoTotal
      });
      worksheet.addRow({
        tipo: "Monto Comisión",
        total: montoSinDescuentos * user.empleado.porcentaje_comision / 100
      });
      worksheet.getColumn('total').numFmt = '#,##0.00';

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(
        blob,
        "Caja Cerrada " + dayjs().format("DD/MM/YYYY H:m:ss") + ".xlsx"
      );

      setLoadingExcel(false);
    } catch (error) {
      console.log(error);
      setLoadingExcel(false);
      toast.error(
        <div>Error al exportar a Excel. Por favor, inténtelo de nuevo.</div>,
        {
          position: "top-center",
          autoClose: 2000
        }
      );
    }
  };

  const columnsPagosCajaActual = [
    {
      name: "idPago",
      label: "ID",
      options: { display: "excluded" },
    },
    {
      name: "cuentas_corriente.cliente.RazonSocial",
      label: "Razón Social",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value}</Row>
        ),
      },
    },
    {
      name: "Tipo",
      label: "Tipo",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value}</Row>
        ),
      },
    },
    {
      name: "Total",
      label: "Total",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>
            {"$" +
              new Intl.NumberFormat("es-AR", {
                style: "decimal",
                currency: "ARS",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(value)}
          </Row>
        ),
      },
    },
  ];

  const columnsPagosHistorialCaja = [
    {
      name: "cliente",
      label: "Razón Social",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value}</Row>
        ),
      },
    },
    {
      name: "tipo",
      label: "Tipo",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value}</Row>
        ),
      },
    },
    {
      name: "total",
      label: "Total",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>
            {"$" +
              new Intl.NumberFormat("es-AR", {
                style: "decimal",
                currency: "ARS",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(value)}
          </Row>
        ),
      },
    },
  ];

  const columnsHistorialDeCajas = [
    {
      name: "idCaja",
      label: "ID",
      options: { display: "excluded" },
    },
    {
      name: "FechaCierre",
      label: "Fecha de Cierre",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>
            {dayjs(value).format("DD/MM/YYYY HH:mm")}
          </Row>
        ),
      },
    },
    {
      name: "Total",
      label: "Total",
      options: {
        customBodyRender: (value) => {
          const nf = new Intl.NumberFormat("es-AR", {
            style: "decimal",
            currency: "ARS",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return nf.format(value);
        },
      },
    },
    {
      name: "ChequeMonto",
      label: "ChequeMonto",
      options: { display: "excluded" },
    },
    {
      name: "TarjetaMonto",
      label: "TarjetaMonto",
      options: { display: "excluded" },
    },
    {
      name: "TransferenciaMonto",
      label: "TransferenciaMonto",
      options: { display: "excluded" },
    },
    {
      name: "EfectivoMonto",
      label: "EfectivoMonto",
      options: { display: "excluded" },
    },
    {
      name: "Observaciones",
      label: "Observaciones",
      options: { display: "excluded" },
    },
    {
      name: "Total",
      label: "Total",
      options: { display: "excluded" },
    },
    {
      name: "FechaCierre",
      label: "Fecha de Cierre",
      options: { display: "excluded" },
    },
  ];

  const optionsHistorialDeCajas = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar Caja",
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    responsive: "standard",
    enableNestedDataAccess: ".",
    onRowClick: async (rowData) => {
      await cargarInformeCaja(rowData);
    },
  };

  const optionsPagosCajaActual = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar pago",
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    responsive: "standard",
    elevation: 0,
    enableNestedDataAccess: ".",
    onRowClick: (rowData) => {
      cargarModalPago(rowData);
    },
  };

  const optionsPagosHistorialCaja = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar pago",
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    responsive: "standard",
    elevation: 0,
    enableNestedDataAccess: ".",
  };

  if (loading) {
    return (
      <Row className="justify-content-center py-4">
        <Spinner animation="border" role="status" />
      </Row>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="dc-root">
      <div className="dc-hero dc-hero-standalone">
        <Row className="align-items-center g-1">
          <Col>
            <div className="dc-hero-title">Dashboard de Cajas</div>
            <div className="dc-hero-subtitle">
              Saldo consolidado y detalle por medio de pago en tiempo real
            </div>
          </Col>
          <Col xs={12} lg="auto">
            <div className="dc-hero-actions">
              <Badge bg="light" text="dark" pill className="dc-badge">
                {cajasAbiertas.length} abiertas
              </Badge>
              <Button
                variant="light"
                className="dc-refresh-btn"
                onClick={exportarExcel}
                disabled={cajasAbiertas.length === 0}
              >
                Exportar Excel
              </Button>
              <Button variant="light" className="dc-refresh-btn" onClick={fetchCajas}>
                Actualizar
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <div className="dc-block">
        <Row className="g-1 mb-1">
          <Col xs={12}>
            <Card className="dc-card dc-kpi-card">
              <Card.Body>
                <div className="dc-kpi-label">SALDO GENERAL</div>
                <div className="dc-kpi-value">
                  {nf.format(resumen.total)}
                </div>
                <div className="dc-kpi-note">
                  Acumulado de todas las cajas abiertas
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <div className="dc-block">
        <Row className="g-1 mb-1">
          {tipos.map((tipo) => (
            <Col key={tipo.key} xs={12} sm={6} lg={3}>
              <Card className="dc-card" style={{ background: tipo.bg }}>
                <Card.Body>
                  <div className="dc-type-head mb-2">
                    <span className="dc-chip" style={{ color: tipo.color, background: "#ffffffaa" }}>
                      <span>{tipo.icon}</span>
                      <span className="dc-chip-label">{tipo.label}</span>
                    </span>
                    <span className="dc-type-percent">
                      {resumen.total > 0
                        ? `${((tipo.value / resumen.total) * 100).toFixed(1)}%`
                        : "0.0%"}
                    </span>
                  </div>
                  <div className="dc-type-value">
                    {nf.format(tipo.value)}
                  </div>
                  <ProgressBar
                    now={resumen.total > 0 ? (tipo.value / resumen.total) * 100 : 0}
                    className="dc-progress"
                    variant="warning"
                  />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div className="dc-block">
        <Row className="g-1 dc-cajas-row">
          {cajasAbiertas.length === 0 ? (
            <Col>
              <Alert variant="warning" className="mb-0">
                No hay cajas abiertas en este momento.
              </Alert>
            </Col>
          ) : (
            cajasAbiertas.map((caja) => (
              <Col key={`${caja.idCaja}-${caja.codigoVendedor}`} xs={12} md={6}>
                <Card className="dc-card dc-caja-card">
                  <Card.Body>
                    <div className="dc-caja-head">
                      <div className="dc-caja-persona">
                        <Card.Title className="dc-caja-title">
                          Caja #{caja.idCaja}
                        </Card.Title>
                        <Card.Subtitle className="text-muted dc-caja-subtitle">
                          {caja.nombreEmpleado} {caja.apellidoEmpleado}
                        </Card.Subtitle>
                      </div>
                      <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                        <Badge
                          bg="light"
                          text="dark"
                          className="dc-vendedor-badge"
                        >
                          Vend. {caja.codigoVendedor}
                        </Badge>
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ color: "#6c757d", lineHeight: 1 }}
                          title="Ver detalle"
                          onClick={() =>
                            cargarModalEmpleado(
                              caja.codigoVendedor,
                              `${caja.nombreEmpleado} ${caja.apellidoEmpleado}`.trim()
                            )
                          }
                        >
                          <Visibility fontSize="large" />
                        </Button>
                      </div>
                    </div>

                    <div className="dc-metrics-grid">
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Efectivo</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.efectivo)}</div>
                      </div>
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Transferencia</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.transferencia)}</div>
                      </div>
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Cheque</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.cheque)}</div>
                      </div>
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Tarjeta</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.tarjeta)}</div>
                      </div>
                    </div>

                    <div className="dc-total-box mt-1">
                      <div className="dc-total-label">Total de caja</div>
                      <div className="dc-total-value">
                        {nf.format(caja.montos.total)}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>
      </div>

      <Modal
        show={showPregunta}
        fullscreen={true}
        onHide={() => setShowPregunta(false)}
      >
        <Modal.Header>
          <Modal.Title>Atención</Modal.Title>
          <Button
            variant="outline-danger"
            onClick={() => setShowPregunta(false)}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body>¿Desea cerrar la caja?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={() => cerrarModalCierraCaja()}
          >
            Cerrar Caja
          </Button>
          <Button
            variant="outline-success"
            onClick={() => setShowPregunta(false)}
          >
            Salir
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showComprobantePagoCC}
        fullscreen={true}
        onHide={() => setShowComprobantePagoCC(false)}
      >
        <Modal.Header>
          <Modal.Title>
            Comprobante: {dataGetPago?.idPago || ""}
          </Modal.Title>
          <Button
            variant="outline-danger"
            onClick={() => setShowComprobantePagoCC(false)}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          {loadingModal ? (
            <Row className="justify-content-center py-4">
              <Spinner animation="border" role="status" />
            </Row>
          ) : (
            <div style={{ padding: "16px" }}>
              {dataGetPago.Tipo === "Cheque" ? (
                <div>
                  <h5 className="mb-3">Detalle del Pago - Cheque</h5>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item"><b>Fecha:</b> {dayjs(dataGetPago.Fecha).format("DD/MM/YY HH:mm:ss")}</li>
                    <li className="list-group-item"><b>Cheque Fecha Pago:</b> {dayjs(dataGetPago.ChequeFechaPago).format("DD/MM/YY")}</li>
                    <li className="list-group-item"><b>Cheque Banco Emisor:</b> {dataGetPago.ChequeBancoEmisor}</li>
                    <li className="list-group-item"><b>Cheque Fecha Vencimiento:</b> {dayjs(dataGetPago.ChequeFechaVencimiento).format("DD/MM/YY")}</li>
                    <li className="list-group-item"><b>Número de Cheque:</b> {dataGetPago.ChequeNroCheque}</li>
                    <li className="list-group-item"><b>Cheque Monto:</b> $ {dataGetPago.ChequeMonto}</li>
                    <li className="list-group-item"><b>Cheque Digital:</b> {dataGetPago.ChequeDigital ? "Si" : "No"}</li>
                    <li className="list-group-item"><b>Observaciones:</b> {dataGetPago.Observaciones}</li>
                  </ul>
                </div>
              ) : dataGetPago.Tipo === "Efectivo" ? (
                <div>
                  <h5 className="mb-3">Detalle del Pago - Efectivo</h5>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item"><b>Fecha:</b> {dayjs(dataGetPago.Fecha).format("DD/MM/YY HH:mm:ss")}</li>
                    <li className="list-group-item"><b>Efectivo Fecha Pago:</b> {dayjs(dataGetPago.EfectivoFechaPago).format("DD/MM/YY")}</li>
                    <li className="list-group-item"><b>Efectivo Monto:</b> $ {dataGetPago.EfectivoMonto}</li>
                    <li className="list-group-item"><b>Observaciones:</b> {dataGetPago.Observaciones}</li>
                  </ul>
                </div>
              ) : dataGetPago.Tipo === "Tarjeta" ? (
                <div>
                  <h5 className="mb-3">Detalle del Pago - Tarjeta</h5>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item"><b>Fecha:</b> {dayjs(dataGetPago.Fecha).format("DD/MM/YY HH:mm:ss")}</li>
                    <li className="list-group-item"><b>Tarjeta Fecha Pago:</b> {dayjs(dataGetPago.TarjetaFechaPago).format("DD/MM/YY")}</li>
                    <li className="list-group-item"><b>Tarjeta Nro Comprobante:</b> {dataGetPago.TarjetaNroComprobante}</li>
                    <li className="list-group-item"><b>Tarjeta Monto:</b> $ {dataGetPago.TarjetaMonto}</li>
                    <li className="list-group-item"><b>Tipo de Tarjeta:</b> {dataGetPago.TipoDeTarjeta}</li>
                    <li className="list-group-item"><b>Observaciones:</b> {dataGetPago.Observaciones}</li>
                  </ul>
                </div>
              ) : dataGetPago.Tipo === "Transferencia" ? (
                <div>
                  <h5 className="mb-3">Detalle del Pago - Transferencia</h5>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item"><b>Fecha:</b> {dayjs(dataGetPago.Fecha).format("DD/MM/YY HH:mm:ss")}</li>
                    <li className="list-group-item"><b>Transferencia Fecha Pago:</b> {dayjs(dataGetPago.TransferenciaFechaPago).format("DD/MM/YY")}</li>
                    <li className="list-group-item"><b>Transferencia Cuenta Destino:</b> {dataGetPago.TransferenciaCuentaDestino}</li>
                    <li className="list-group-item"><b>Transferencia Nro Comprobante:</b> {dataGetPago.TransferenciaNroComprobante}</li>
                    <li className="list-group-item"><b>Transferencia Monto:</b> $ {dataGetPago.TransferenciaMonto}</li>
                    <li className="list-group-item"><b>Observaciones:</b> {dataGetPago.Observaciones}</li>
                  </ul>
                </div>
              ) : dataGetPago.Tipo === "Descuento" ? (
                <div>
                  <h5 className="mb-3">Detalle del Descuento</h5>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item"><b>Fecha:</b> {dayjs(dataGetPago.Fecha).format("DD/MM/YY HH:mm:ss")}</li>
                    <li className="list-group-item"><b>Descuento Monto:</b> $ {dataGetPago.Total}</li>
                    <li className="list-group-item"><b>Observaciones Descuento:</b> {dataGetPago.ObservacionesDescuento}</li>
                  </ul>
                </div>
              ) : dataGetPago.Tipo === "Nota De Débito" ? (
                <div>
                  <h5 className="mb-3">Detalle de la Nota De Débito</h5>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item"><b>Fecha:</b> {dayjs(dataGetPago.Fecha).format("DD/MM/YY HH:mm:ss")}</li>
                    <li className="list-group-item"><b>Nota De Débito Monto:</b> $ {dataGetPago.Total}</li>
                    <li className="list-group-item"><b>Observaciones Nota de débito:</b> {dataGetPago.ObservacionesNDD}</li>
                  </ul>
                </div>
              ) : null}
              <div className="d-flex justify-content-center mt-4">
                <Button variant="outline-danger" onClick={() => eliminarPago()}>
                  Eliminar
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-success"
            onClick={() => setShowComprobantePagoCC(false)}
          >
            Salir
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInformeCaja}
        fullscreen={true}
        onHide={() => setShowInformeCaja(false)}
      >
        <Modal.Header>
          <Modal.Title>
            Informe de Caja N° {informeCaja[0]}
          </Modal.Title>
          <Button
            variant="outline-danger"
            onClick={() => cerrarModalInformeCaja()}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Row className="d-flex justify-content-center">
            <h4>Datos de la caja</h4>
          </Row>
          <Row className="d-flex justify-content-center">
            <ul>
              <li>
                Fecha de Cierre: <b>{informeCaja[9]}</b>
              </li>
              <li>
                Monto Efectivo: <b>$ {informeCaja[6]}</b>
              </li>
              <li>
                Monto Transferencias: <b>$ {informeCaja[5]}</b>
              </li>
              <li>
                Monto Cheques: <b>$ {informeCaja[3]}</b>
              </li>
              <li>
                Monto Tarjetas: <b>$ {informeCaja[4]}</b>
              </li>
              <li>
                Monto Total: <b>$ {informeCaja[8]}</b>
              </li>
              <li>
                Observación: <b> {informeCaja[7]}</b>
              </li>
            </ul>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                style={{ backgroundColor: "#f5be87" }}
              >
                <Typography
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <b>Pagos de la caja</b>
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <MUIDataTable
                  ref={containerRef}
                  title={"Pagos caja"}
                  data={historialPagosCaja}
                  columns={columnsPagosHistorialCaja}
                  options={optionsPagosHistorialCaja}
                />
              </AccordionDetails>
            </Accordion>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="info"
            onClick={exportarExcelCajaCerrada}
            disabled={loadingExcel}
            style={{ marginRight: "8px" }}
          >
            {loadingExcel ? "Generando..." : "Exportar a Excel"}
          </Button>
          <Button
            variant="outline-success"
            onClick={() => cerrarModalInformeCaja()}
          >
            Salir
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModalEmpleado}
        fullscreen={true}
        onHide={() => setShowModalEmpleado(false)}
      >
        <Modal.Header>
          <Modal.Title>Empleado: {dataElegido[1]}</Modal.Title>
          <Button variant="outline-danger" onClick={() => cerrarModal()}>
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          {loadingModal ? (
            <Row className="justify-content-center py-4">
              <Spinner animation="border" role="status" />
            </Row>
          ) : (
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              id="dashboard-caja-tabs"
              className="mb-3"
            >
              <Tab eventKey="actual" title="Actual">
                <Col>
                  <Row className="d-flex justify-content-center">
                    <h4>Caja Actual</h4>
                  </Row>
                  <Row className="d-flex justify-content-center">
                    <ul>
                      <li>
                        Monto Efectivo:{" "}
                        {cajaActual.montos !== undefined ? (
                          <b>{" $" + cajaActual.montos.montoEfectivo}</b>
                        ) : (
                          <b>$0</b>
                        )}
                      </li>
                      <li>
                        Monto Transferencias:{" "}
                        {cajaActual.montos !== undefined ? (
                          <b>
                            {" $" + cajaActual.montos.montoTransferencia}
                          </b>
                        ) : (
                          <b>$0</b>
                        )}
                      </li>
                      <li>
                        Monto Cheques:{" "}
                        {cajaActual.montos !== undefined ? (
                          <b>{" $" + cajaActual.montos.montoCheque}</b>
                        ) : (
                          <b>$0</b>
                        )}
                      </li>
                      <li>
                        Monto Tarjetas:{" "}
                        {cajaActual.montos !== undefined ? (
                          <b>{" $" + cajaActual.montos.montoTarjeta}</b>
                        ) : (
                          <b>$0</b>
                        )}
                      </li>
                      <li>
                        Monto Total:{" "}
                        {cajaActual.montos !== undefined ? (
                          <b>{" $" + cajaActual.montos.montoTotal}</b>
                        ) : (
                          <b>$0</b>
                        )}
                      </li>
                    </ul>
                  </Row>
                  <Row className="d-flex justify-content-center">
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        style={{ backgroundColor: "#f5be87" }}
                      >
                        <Typography
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <b>Pagos</b>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails style={{ padding: 0 }}>
                        <MUIDataTable
                          ref={containerRef}
                          title={"Pagos caja actual"}
                          data={cajaActual.pagos}
                          columns={columnsPagosCajaActual}
                          options={optionsPagosCajaActual}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Row>
                  <Row
                    className="d-flex justify-content-center"
                    style={{ paddingTop: 10 }}
                  >
                    <textarea
                      id="observacion-dashboard"
                      placeholder="Observación"
                      rows="5"
                      cols="27"
                    />
                  </Row>
                  <Row
                    className="d-flex justify-content-center"
                    style={{ padding: 10, gap: 10 }}
                  >
                    <Button
                      variant="danger"
                      onClick={() => setShowPregunta(true)}
                    >
                      Cerrar Caja
                    </Button>
                    <Button
                      variant="info"
                      onClick={() => exportarExcelCajaAbierta()}
                      disabled={loadingExcel}
                    >
                      {loadingExcel ? "Exportando..." : "Exportar Caja Abierta"}
                    </Button>
                  </Row>
                </Col>
              </Tab>
              <Tab eventKey="historicoCajas" title="Histórico Cajas">
                <MUIDataTable
                  ref={containerRef}
                  title={"Historial de Cajas"}
                  data={historialDeCajas}
                  columns={columnsHistorialDeCajas}
                  options={optionsHistorialDeCajas}
                />
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-success" onClick={() => cerrarModal()}>
            Salir
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardCajas;
